import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { isTauri } from './utils/tauriPrint.js';
import { AppErrorBoundary } from './components/AppErrorBoundary.tsx';
import { initEyeCare } from './lib/eyeCare';
import { initCustomAlert } from './lib/customAlert';

initEyeCare();
initCustomAlert();

// ── PWA Zoom Lock ─────────────────────────────────────────────────────────────
// Desktop PWA mein keyboard aur mouse wheel zoom completely band karo
(function blockZoom() {
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key === '+' || e.key === '-' || e.key === '=' || e.key === '0' ||
          e.key === '_' || e.code === 'NumpadAdd' || e.code === 'NumpadSubtract') {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }
  }, { capture: true, passive: false });

  document.addEventListener('wheel', (e: WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }, { capture: true, passive: false });

  document.addEventListener('touchstart', (e: TouchEvent) => {
    if (e.touches.length > 1) e.preventDefault();
  }, { passive: false });

  document.addEventListener('touchmove', (e: TouchEvent) => {
    if (e.touches.length > 1) e.preventDefault();
  }, { passive: false });
})();

// ── Root Layout — window ko hamesha fill karo (Tauri + Browser dono ke liye) ──
// PWA / Tauri mein window.resizeTo() kaam nahi karta aur CSS scale se
// chhoti window mein content clip hota tha — isliye sirf #root ko 100% fill
// karte hain, size lock bilkul nahi. Minimum window size Tauri config
// (tauri.conf.json → windows[].minWidth / minHeight) se control hogi.
(function applyRootLayout() {
  const applyLayout = () => {
    const root = document.getElementById('root');
    if (!root) return;
    root.style.width           = '100%';
    root.style.height          = '100vh';
    root.style.transform       = 'none';
    root.style.transformOrigin = 'top left';
    document.documentElement.style.setProperty('--app-scale', '1');
  };

  applyLayout();
  document.addEventListener('DOMContentLoaded', applyLayout);
  window.addEventListener('load', applyLayout);
})();

// ── Global Error Guard — crash silently log karo, blank screen avoid karo ──
window.addEventListener('unhandledrejection', (e) => {
  // Firebase / network errors silently absorb karo
  const msg = e.reason?.message || '';
  if (
    msg.includes('Failed to fetch') ||
    msg.includes('NetworkError') ||
    msg.includes('firestore') ||
    msg.includes('quota')
  ) {
    e.preventDefault(); // Console mein bhi nahi aayega
    return;
  }
  console.error('[Aarthika] Unhandled promise rejection:', e.reason);
});

window.addEventListener('error', (e) => {
  // Script load failures (CDN down, etc.) gracefully absorb
  if (e.message?.includes('Loading chunk') || e.message?.includes('Loading module')) {
    console.warn('[Aarthika] Chunk load error — reloading...');
    setTimeout(() => window.location.reload(), 1500);
    e.preventDefault();
  }
});

// ── Offline / Online Toast (brief only) ──────────────────────────────────────
(function setupNetworkBanner() {
  let toast: HTMLDivElement | null = null;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const showToast = (msg: string, color: string, duration = 2500) => {
    if (toast) { toast.remove(); toast = null; }
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }

    toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed; bottom: 30px; left: 50%; transform: translateX(-50%);
      z-index: 999999; background: ${color}; color: white;
      font-size: 12px; font-weight: 600; padding: 6px 16px;
      border-radius: 20px; font-family: Inter, sans-serif;
      box-shadow: 0 2px 8px rgba(0,0,0,0.25);
      opacity: 1; transition: opacity 0.4s ease;
    `;
    toast.textContent = msg;
    document.body.appendChild(toast);

    hideTimer = setTimeout(() => {
      if (toast) { toast.style.opacity = '0'; }
      setTimeout(() => { toast?.remove(); toast = null; }, 400);
    }, duration);
  };

  window.addEventListener('offline', () => {
    showToast('Offline mode activated', '#b45309');
  });

  window.addEventListener('online', () => {
    showToast('Back online', '#1e8585');
  });

  // Initial check — sirf brief toast, no persistent banner
  if (!navigator.onLine) {
    showToast('Offline mode activated', '#b45309');
  }
})();

// ── Render ────────────────────────────────────────────────────────────────────
createRoot(document.getElementById('root')!).render(
  <AppErrorBoundary>
    <App />
  </AppErrorBoundary>
);

// ── PWA Service Worker ────────────────────────────────────────────────────────
// Tauri app mein SW register nahi karna — sirf browser/PWA ke liye
if (!isTauri() && 'serviceWorker' in navigator) {
  import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({
      onNeedRefresh() {
        // Naya version available — silently update
        // (user ko disturb nahi karna, next reload pe lagega)
      },
      onOfflineReady() {
        // App is ready for offline use
      },
      onRegisterError(err) {
        console.warn('[Aarthika] SW register failed:', err);
      },
    });
  }).catch(() => {
    // PWA not supported — silently ignore
  });
}